import {
  MAX_OFFLINE_QUEUE_SIZE,
  MUTATION_MAX_FAILURE_COUNT,
  OFFLINE_QUEUE_META,
} from './constants';
import { shouldRetryOfflineMutation } from './offlineMutationOptions';

function isOfflineQueuedMutation(mutation) {
  return Boolean(mutation?.options?.meta?.offlineQueue);
}

function isActiveOfflineQueuedMutation(mutation) {
  const { status, isPaused, failureCount, failureReason, error } = mutation.state;
  if (status === 'pending' || isPaused) return true;
  if (status === 'error') {
    const lastError = failureReason ?? error;
    return (
      failureCount < MUTATION_MAX_FAILURE_COUNT &&
      shouldRetryOfflineMutation(failureCount, lastError)
    );
  }
  return false;
}

/** Count paused/pending offline mutations in the cache. */
export function countOfflineQueuedMutations(queryClient) {
  if (!queryClient) return 0;
  return queryClient
    .getMutationCache()
    .getAll()
    .filter(
      (mutation) => isOfflineQueuedMutation(mutation) && isActiveOfflineQueuedMutation(mutation),
    ).length;
}

export function isOfflineQueueFull(queryClient) {
  return countOfflineQueuedMutations(queryClient) >= MAX_OFFLINE_QUEUE_SIZE;
}

/** Persist paused, pending, retrying, and terminal-failed offline mutations. */
export function shouldDehydrateMutation(mutation) {
  if (!isOfflineQueuedMutation(mutation)) return false;

  const { status, isPaused, failureCount, failureReason, error } = mutation.state;
  if (status === 'pending' || isPaused) return true;
  if (status === 'error') {
    const lastError = failureReason ?? error;
    if (failureCount >= MUTATION_MAX_FAILURE_COUNT) return true;
    return shouldRetryOfflineMutation(failureCount, lastError);
  }
  return false;
}

/**
 * A save stored while in flight comes back "pending" but not paused, so nothing ever runs it (resumePausedMutations
 * only continues paused ones) - and it holds its scope, so every later save in it waits forever. Sign-in remounts the
 * persist provider, which restored the just-sent Going this way and left Create a group spinning (2026-10-23). Restored
 * as paused, the restore's own resume re-runs it and the queue behind it moves.
 */
export function pauseRestoredPendingMutations(client) {
  for (const m of client?.clientState?.mutations ?? []) {
    if (m?.state?.status === 'pending' && !m.state.isPaused)
      m.state = { ...m.state, isPaused: true };
  }
  return client;
}

export function assertQueueHasCapacity(queryClient) {
  if (isOfflineQueueFull(queryClient)) {
    const err = new Error('Offline queue is full. Wait for pending actions to sync.');
    err.code = 'OFFLINE_QUEUE_FULL';
    throw err;
  }
}

export { OFFLINE_QUEUE_META };
